import bus from '@/plugins/eventbus'
import Preferences from '@/plugins/preferences'
import { stateGetters, stateSetters, watchGetterAndPersist } from '@/utils/store/store-utils'
import getBrowser from '@/plugins/browser-polyfill'

import User from '@/store/models/user/User'

import { MAX_IMPORT_LIMIT } from '@/utils/constants'
import mutations from './mutations'
import actions from './actions'

const defaultState = {
  userId: undefined, // initialize as undefined to say it was not fetched yet
  accessToken: null,
  refreshToken: null,
  sessionId: null, // NOTE might not be needed yet
  onboardingConsent: undefined,
  collectionIdCache: undefined,
  guestAccountNoticeDismissed: undefined,
}

export default {
  name: 'auth',
  namespaced: true,
  state: () => defaultState,
  getters: {
    ...stateGetters(defaultState),
    session(state) {
      if (!state.userId) return null
      return User.query().with('*').find(state.userId)
    },
    hasFullAccount(_, getters) {
      return getters.session?.isFullAccount ?? false
    },
    showGuestAccountNotice(state, getters, _rs, rootGetters) {

      if (!rootGetters['device/online'] ||  !rootGetters['device/localeLoaded']) return false
      if (getters.session?.isFullAccount || !getters.session) return false

      // If the notice was dismissed (value is true), don't show it
      if (state.guestAccountNoticeDismissed === true) return false

      // If there's no entry (undefined), show the notice
      return true
    },
    collectionId(s, getters) {
      return getters.session?.collectionId ?? s.collectionIdCache
    },
    importCredits(s, getters) {
      return getters.session?.importCredits ?? MAX_IMPORT_LIMIT
    },
  },
  mutations: {
    ...stateSetters(defaultState),
    ...mutations,
  },
  actions,
}

async function initAuth(app, accessToken) {
  if (!accessToken) {
    app.config.globalProperties.$store.commit('auth/setUserId', null)
  } else {
    try {
      await app.config.globalProperties.$store.dispatch('auth/checkOrRefreshAuth')

      // Load guest account notice preference after userId is available
      const userId = app.config.globalProperties.$store.getters['auth/userId']
      if (userId) {
        const guestAccountNoticeDismissed = await Preferences.get(`showGuestAccountNotice:${userId}`)
        app.config.globalProperties.$store.commit('auth/setGuestAccountNoticeDismissed', guestAccountNoticeDismissed)
      }

      // if device is online, try to get up-to-date user data
      if (app.config.globalProperties.$store.getters['device/online']) {
        await app.config.globalProperties.$store.dispatch('auth/getMe')
      }
    } catch (error) {
      app.config.globalProperties.$error.toast(error)

      // the error system above handles SESSION_EXPIRED log out
      if (error.message !== 'SESSION_EXPIRED') {
        // if any error happens during auth boot, we log user out to be safe and avoid user getting stuck in loop
        app.config.globalProperties.$store.dispatch('auth/logout', false)
      }
    }
  }
}

bus.$once('app:created', async app => {
  const [accessToken, refreshToken, onboardingConsent, collectionId] = await Promise.all([
    Preferences.get('accessToken'),
    Preferences.get('refreshToken'),
    Preferences.get('onboardingConsent'),
    Preferences.get('collectionId'),
  ])

  app.config.globalProperties.$store.commit('auth/setAccessToken', accessToken)
  app.config.globalProperties.$store.commit('auth/setRefreshToken', refreshToken)
  app.config.globalProperties.$store.commit('auth/setOnboardingConsent', onboardingConsent)
  app.config.globalProperties.$store.commit('auth/setCollectionIdCache', collectionId)

  // state persisters
  watchGetterAndPersist(app.config.globalProperties.$store, 'auth/accessToken', 'accessToken')
  watchGetterAndPersist(app.config.globalProperties.$store, 'auth/refreshToken', 'refreshToken')
  watchGetterAndPersist(app.config.globalProperties.$store, 'auth/onboardingConsent', 'onboardingConsent')
  watchGetterAndPersist(app.config.globalProperties.$store, 'auth/collectionId', 'collectionId')

  // Watch for guestAccountNoticeDismissed changes and persist with userId in the key
  app.config.globalProperties.$store.watch(
    (state) => state.auth.guestAccountNoticeDismissed,
    async (value) => {
      const userId = app.config.globalProperties.$store.getters['auth/userId']
      if (userId && value !== undefined) {
        await Preferences.set(`showGuestAccountNotice:${userId}`, value)
      }
    }
  )

  // Watch for userId changes and reload the guest account notice preference
  app.config.globalProperties.$store.watch(
    (_, getters) => getters['auth/userId'],
    async (userId) => {
      if (userId) {
        const guestAccountNoticeDismissed = await Preferences.get(`showGuestAccountNotice:${userId}`)
        app.config.globalProperties.$store.commit('auth/setGuestAccountNoticeDismissed', guestAccountNoticeDismissed)
      } else {
        app.config.globalProperties.$store.commit('auth/setGuestAccountNoticeDismissed', undefined)
      }
    }
  )

  // chrome extension auth refresh
  if (BUILD_TARGET === 'extension') {
    (await getBrowser()).runtime.onMessage.addListener(async ({ event, data }) => {
      console.log(`Received event ${event} from extension background worker`, data)

      switch (event) {
        case 'app:auth-refresh':
          await app.config.globalProperties.$store.dispatch('auth/handleAuthTokens', data)
          bus.$emit('extension:auth-refresh', data)
          break
        case 'app:auth-logout':
          await app.config.globalProperties.$store.dispatch('auth/logout')
          break
        case 'app:sync-device-id':
          await app.config.globalProperties.$store.dispatch('device/setDeviceId', data.id)
          break
        default: break
      }
    })
  }

  // init auth
  initAuth(app, accessToken)

  // re-try auth if device comes online and no session data
  app.config.globalProperties.$store.watch((_, getters) => getters['device/online'], () => {
    if (!app.config.globalProperties.$store.getters['auth/session']) {
      console.log('[AUTH] Device is online, re-trying auth')
      initAuth(app, accessToken)
    }
  })
})
